import React, { useRef, useState, useEffect } from 'react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import { OverlayTrigger, Popover } from 'react-bootstrap-5';
import { type OverlayTriggerType } from 'react-bootstrap-5/esm/OverlayTrigger';
import { type Placement } from 'react-bootstrap-5/esm/types';
import { type DOMContainer } from '@restart/ui/useWaitForDOMRef';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { Button } from '@buildoutinc/blueprint-react/ui/Button';

import './styles.scss';

type FilterButtonProps = {
  buttonIcon?: IconProp;
  children: React.ReactNode;
  className?: string;
  container?: DOMContainer;
  containerClasses?: string;
  disabled?: boolean;
  filtersApplied?: boolean;
  filterTag?: string;
  label: string | React.ReactNode;
  name: string;
  placement?: Placement;
  popoverBodyClassName?: string;
  selectedLength?: number;
  style?: React.CSSProperties;
};

export default function FilterButton({
  buttonIcon,
  children,
  className,
  container,
  containerClasses,
  disabled,
  filtersApplied,
  name,
  label,
  placement,
  popoverBodyClassName,
  style = {}
}: FilterButtonProps) {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setTriggerWidth(containerRef.current.offsetWidth);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderPopover = () => (
    <Popover
      className={name}
      id={name}
      style={{
        width: triggerWidth ? `${triggerWidth}px` : 'auto',
        maxWidth: 'none'
      }}
    >
      <Popover.Body className={classNames(popoverBodyClassName)} style={{ maxHeight: '600px' }}>
        {children}
      </Popover.Body>
    </Popover>
  );

  const buttonClasses = `
    filter-button d-flex align-items-center justify-content-between form-control
    ${filtersApplied ? 'has-filters' : ''}
    ${className || ''}
  `;

  const trigger: OverlayTriggerType | OverlayTriggerType[] = disabled ? [] : 'click';

  return (
    <div
      className={containerClasses}
      ref={containerRef}
      style={style}
    >
      <OverlayTrigger
        container={container}
        overlay={renderPopover()}
        placement={placement || 'bottom'}
        rootClose
        trigger={trigger}
      >

        <Button
          className={buttonClasses}
          ref={buttonRef}
          style={{ ...style, '--trigger-width': `${triggerWidth}px` } as React.CSSProperties}
          variant="outline"
        >
          {buttonIcon && <FontAwesomeIcon icon={buttonIcon} />}
          {typeof label === 'string'
            ? <span ref={textRef}>{label}</span>
            : label
          }
          <FontAwesomeIcon icon={faCaretDown} />
        </Button>
      </OverlayTrigger>
    </div>
  );
}
